// ============================================================================
// FSA — "Re-round all" dialog
// ============================================================================
// Engagement-level bulk re-round after the rounding factor changed. Shows
// the plan (what will be re-rounded, in which order, what is skipped and
// why), runs it with per-item progress, stops at the first failure with a
// Resume, and ends on a summary that lists statement overrides kept as
// typed (they were typed under the old factor — the auditor reviews them).
// Logic: components/modules/fsa/engagement/reround/bulkReround.ts.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ID } from 'api/types';
import fsaNotesService from 'api/services/fsa/fsaNotesService';
import fsaStatementsService from 'api/services/fsa/fsaStatementsService';
import {
  planReround,
  runReround,
  type ReroundDeps,
  type ReroundItem,
} from 'components/modules/fsa/engagement/reround/bulkReround';

interface Props {
  isOpen: boolean;
  engagementId: ID;
  /** Current factor, for the dialog text (e.g. "Thousands"). */
  factorLabel?: string | null;
  /** Shown above the plan when opened right after a factor change in Settings. */
  promptReason?: string | null;
  onClose: () => void;
  /** After a run (complete or stopped) — lets the caller refresh lists. */
  onRan?: () => void;
}

const deps: ReroundDeps = {
  listNotes: async e => (await fsaNotesService.list(e)).data,
  hydrateNote: async (e, id) => (await fsaNotesService.hydrate(e, id)).data,
  saveNote: (e, id, req) => fsaNotesService.save(e, id, req),
  listStatements: async e => (await fsaStatementsService.list(e)).data,
  hydrateStatement: async (e, id) => (await fsaStatementsService.hydrate(e, id)).data,
  saveStatement: (e, id, req) => fsaStatementsService.save(e, id, req),
};

const PHASE_LABEL: Record<ReroundItem['phase'], string> = {
  1: 'Notes',
  2: 'Statements read by notes',
  3: 'Notes that read a statement',
  4: 'Statements',
};

const FsaReroundAllModal: React.FC<Props> = ({ isOpen, engagementId, factorLabel, promptReason, onClose, onRan }) => {
  const [items, setItems] = useState<ReroundItem[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setItems(null);
    setLoadError(null);
    planReround(engagementId, deps)
      .then(plan => { if (!cancelled) setItems(plan); })
      .catch(err => { if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Could not prepare the list.'); });
    return () => { cancelled = true; };
  }, [isOpen, engagementId]);

  const start = useCallback(async () => {
    if (!items) return;
    setRunning(true);
    try {
      const result = await runReround(engagementId, items, deps, setItems);
      setItems(result);
    } finally {
      setRunning(false);
      onRan?.();
    }
  }, [items, engagementId, onRan]);

  const counts = useMemo(() => {
    const c = { pending: 0, done: 0, skipped: 0, failed: 0 };
    for (const i of items ?? []) {
      if (i.status === 'running') c.pending += 1;
      else c[i.status] += 1;
    }
    return c;
  }, [items]);
  const failed = items?.find(i => i.status === 'failed');
  const finished = !!items && counts.pending === 0 && !failed && !running;
  const overrides = (items ?? []).filter(i => (i.keptOverrides?.length ?? 0) > 0);

  if (!isOpen) return null;

  const work = (items ?? []).filter(i => i.status !== 'skipped');
  const skipped = (items ?? []).filter(i => i.status === 'skipped');

  return (
    <>
      <div className="modal-backdrop" onClick={running ? undefined : onClose} />
      <div className="modal-container modal-lg">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <IconSystem.refresh size={18} />
            <h2 className="modal-title" style={{ margin: 0 }}>Re-round all notes and statements</h2>
          </div>
          <button className="modal-close" onClick={onClose} disabled={running}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {promptReason && <div style={infoStyle}>{promptReason}</div>}
          <div style={{ fontSize: '0.85rem', lineHeight: 1.5 }}>
            Every saved note is re-rounded{factorLabel ? <> under <strong>{factorLabel}</strong></> : null} exactly as
            its own <strong>Reset rounding</strong> would do, then saved; statements are refreshed from the notes and
            saved, in dependency order. Only rounded figures change — no ledger, TB or cross-note values are pulled.
          </div>
          <div style={warnStyle}>
            <IconSystem.alert size={14} /> Manual rounding adjustments on notes and disclosures are replaced by the
            fresh rounded figures. Statement lines you overrode are kept as typed and listed at the end for review.
          </div>

          {!items && !loadError && <div style={mutedStyle}>Preparing the list…</div>}
          {loadError && <div style={errorStyle}>{loadError}</div>}

          {items && (
            <>
              <div style={mutedStyle}>
                {work.length} to re-round · {counts.done} done · {skipped.length} skipped
                {failed ? ' · stopped on a failure' : ''}
              </div>
              <div style={listStyle}>
                {work.map((i, idx) => (
                  <React.Fragment key={i.key}>
                    {(idx === 0 || work[idx - 1].phase !== i.phase) && (
                      <div style={phaseStyle}>{PHASE_LABEL[i.phase]}</div>
                    )}
                    <div style={rowStyle}>
                      <span style={statusStyle(i.status)}>{STATUS_TEXT[i.status]}</span>
                      <span style={{ flex: 1 }}>{i.name}</span>
                      {i.reason && <span style={{ color: '#b91c1c', fontSize: '0.75rem' }}>{i.reason}</span>}
                    </div>
                  </React.Fragment>
                ))}
                {skipped.length > 0 && <div style={phaseStyle}>Skipped</div>}
                {skipped.map(i => (
                  <div key={i.key} style={rowStyle}>
                    <span style={statusStyle('skipped')}>skipped</span>
                    <span style={{ flex: 1 }}>{i.name}</span>
                    <span style={mutedStyle}>{i.reason}</span>
                  </div>
                ))}
              </div>
              {overrides.length > 0 && (
                <div style={warnStyle}>
                  Overridden lines kept as typed — review them under the new factor:
                  {overrides.map(o => (
                    <div key={o.key} style={{ marginTop: 2 }}>
                      <strong>{o.name}</strong>: {o.keptOverrides!.join(', ')}
                    </div>
                  ))}
                </div>
              )}
              {finished && counts.done > 0 && (
                <div style={infoStyle}>Done — {counts.done} re-rounded and saved.</div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={running}>
            {finished ? 'Close' : 'Cancel'}
          </button>
          {!finished && (
            <button
              className="btn btn-primary"
              onClick={start}
              disabled={running || !items || work.length === 0}
            >
              <IconSystem.refresh size={16} /> {failed ? 'Resume' : running ? 'Re-rounding…' : 'Re-round all'}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

const STATUS_TEXT: Record<ReroundItem['status'], string> = {
  pending: 'pending', running: 'working…', done: 'done', skipped: 'skipped', failed: 'failed',
};

const statusStyle = (s: ReroundItem['status']): React.CSSProperties => ({
  width: 70,
  fontSize: '0.7rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  color: s === 'done' ? '#15803d' : s === 'failed' ? '#b91c1c' : s === 'running' ? '#2563eb' : 'var(--text-secondary)',
});
const listStyle: React.CSSProperties = {
  maxHeight: 340, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 6, padding: '4px 8px',
};
const phaseStyle: React.CSSProperties = {
  fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3,
  color: 'var(--text-secondary)', marginTop: 8, marginBottom: 2,
};
const rowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.82rem', padding: '2px 0',
};
const mutedStyle: React.CSSProperties = { fontSize: '0.78rem', color: 'var(--text-secondary)' };
const warnStyle: React.CSSProperties = {
  fontSize: '0.8rem', padding: '6px 10px', borderRadius: 6, background: '#fef3c7', color: '#92400e',
};
const infoStyle: React.CSSProperties = {
  fontSize: '0.8rem', padding: '6px 10px', borderRadius: 6, background: '#dbeafe', color: '#1e40af',
};
const errorStyle: React.CSSProperties = { fontSize: '0.8rem', color: '#b91c1c' };

export default FsaReroundAllModal;
